import type {
  Attendee,
  ContentItem,
  EventForm,
  EventInfo,
  Session,
  Speaker,
} from './types'

/**
 * Demo dataset: a fictional Vericast Executive Forum. This lets the entire
 * attendee experience render and be demoed without a live backend. When the
 * API lands, the service layer (services/eventService.ts) swaps these for
 * real calls with the same shapes.
 */

// Anchor the agenda to "today" so the schedule always looks current in a demo.
const today = new Date()
function at(dayOffset: number, hh: number, mm = 0): string {
  const d = new Date(today)
  d.setDate(d.getDate() + dayOffset)
  d.setHours(hh, mm, 0, 0)
  return d.toISOString()
}

export const attendee: Attendee = {
  name: 'Jordan Avery',
  firstName: 'Jordan',
  email: 'jordan.avery@northbank.example',
  company: 'Northbank Financial',
  role: 'Chief Marketing Officer',
  track: 'Executive Briefing',
  initials: 'JA',
}

export const eventInfo: EventInfo = {
  id: 'exec-forum-2026',
  name: 'Vericast Executive Forum 2026',
  tagline: 'Growth, intelligence, and the future of financial marketing',
  heroImage: '/demo/hero.jpg',
  startsAt: at(0, 8, 0),
  endsAt: at(1, 16, 0),
  timezone: 'America/Chicago',
  location: 'The Adolphus, Dallas, TX',
  venue: {
    name: 'The Adolphus Hotel',
    address: '1321 Commerce St',
    city: 'Dallas, TX 75202',
    mapUrl: 'https://maps.google.com/?q=The+Adolphus+Dallas',
  },
  welcomeMessage:
    "Welcome to the Executive Forum. We've curated two days of insight, connection, and strategy tailored for financial-institution leaders. Everything you need is right here — your personalized agenda, speaker briefings, resources, and the forms we'll need from you along the way.",
  dressCode: 'Business casual. Evening reception is smart casual.',
  travel:
    'Complimentary valet parking is available at the Commerce St entrance. The Adolphus is a 25-minute drive from Dallas Love Field (DAL) and 30 minutes from DFW International.',
  faqs: [
    {
      question: 'Where do I check in?',
      answer:
        'Registration opens at 8:00 AM in the Grand Ballroom Foyer on Day 1. Bring a photo ID.',
    },
    {
      question: 'Will sessions be recorded?',
      answer:
        'Keynotes and main-stage sessions will be recorded and posted to the Content Hub within 48 hours.',
    },
    {
      question: 'Who do I contact for dietary needs?',
      answer:
        'Please complete the Meal Preferences form, or reach out to your event manager listed under Key Contacts.',
    },
  ],
  contacts: [
    {
      name: 'Morgan Ellis',
      role: 'Event Manager, Vericast',
      email: 'morgan.ellis@vericast.example',
      phone: '+1 (214) 555-0142',
    },
    {
      name: 'Taylor Reed',
      role: 'Client Relationship Director',
      email: 'taylor.reed@vericast.example',
    },
  ],
}

export const speakers: Speaker[] = [
  {
    id: 'sp-1',
    name: 'Dr. Alicia Moreno',
    title: 'Chief Economist',
    company: 'Vericast',
    initials: 'AM',
    photoUrl: '/demo/speaker-am.jpg',
    avatarColor: '#0075BC',
    featured: true,
    bio: 'Dr. Moreno leads Vericast’s economic research practice, advising financial institutions on macro trends, consumer behavior, and growth strategy. She is a frequent keynote speaker and published author on the future of retail banking.',
  },
  {
    id: 'sp-2',
    name: 'Marcus Webb',
    title: 'EVP, Digital Strategy',
    company: 'Meridian Credit Union',
    initials: 'MW',
    photoUrl: '/demo/speaker-mw.jpg',
    avatarColor: '#1C5580',
    featured: true,
    bio: 'Marcus has led digital transformation across three financial institutions, growing digital engagement by over 300%. He focuses on practical, member-first innovation.',
  },
  {
    id: 'sp-3',
    name: 'Priya Nair',
    title: 'Head of Data Science',
    company: 'Vericast',
    initials: 'PN',
    photoUrl: '/demo/speaker-pn.jpg',
    avatarColor: '#5BC6CC',
    bio: 'Priya builds the predictive models behind Vericast’s audience intelligence, turning first-party data into measurable marketing outcomes for banks and credit unions.',
  },
  {
    id: 'sp-4',
    name: 'James Callahan',
    title: 'President & CEO',
    company: 'First Horizon Bank',
    initials: 'JC',
    photoUrl: '/demo/speaker-jc.jpg',
    avatarColor: '#3D6074',
    bio: 'A 25-year veteran of community banking, James champions relationship-driven growth and the role of brand in an increasingly digital marketplace.',
  },
  {
    id: 'sp-5',
    name: 'Elena Vasquez',
    title: 'VP, Customer Experience',
    company: 'Summit Financial',
    initials: 'EV',
    photoUrl: '/demo/speaker-ev.jpg',
    avatarColor: '#E47720',
    bio: 'Elena designs end-to-end customer journeys that blend digital convenience with the human touch, earning her team multiple industry CX awards.',
  },
]

export const sessions: Session[] = [
  {
    id: 's-1',
    title: 'Registration & Welcome Breakfast',
    startsAt: at(0, 8, 0),
    endsAt: at(0, 9, 0),
    location: 'Grand Ballroom Foyer',
    type: 'meal',
    speakerIds: [],
  },
  {
    id: 's-2',
    title: 'Opening Keynote: The Year Ahead in Financial Marketing',
    description:
      'A data-driven look at the forces reshaping how banks and credit unions grow — from shifting consumer expectations to the AI-enabled marketing stack.',
    startsAt: at(0, 9, 0),
    endsAt: at(0, 10, 0),
    location: 'Grand Ballroom',
    track: 'Executive Briefing',
    type: 'keynote',
    speakerIds: ['sp-1'],
    saved: true,
  },
  {
    id: 's-3',
    title: 'Panel: Winning the Battle for Primary Financial Relationships',
    description:
      'Leaders from across the industry debate what it takes to be the account that customers actually use — and how marketing drives it.',
    startsAt: at(0, 10, 30),
    endsAt: at(0, 11, 30),
    location: 'Grand Ballroom',
    track: 'Executive Briefing',
    type: 'panel',
    speakerIds: ['sp-2', 'sp-4', 'sp-5'],
    saved: true,
  },
  {
    id: 's-4',
    title: 'Networking Lunch',
    startsAt: at(0, 11, 30),
    endsAt: at(0, 13, 0),
    location: 'Terrace Restaurant',
    type: 'meal',
    speakerIds: [],
  },
  {
    id: 's-5',
    title: 'Workshop: Turning First-Party Data into Growth',
    description:
      'A hands-on session on building audience segments that convert, led by our data science team.',
    startsAt: at(0, 13, 0),
    endsAt: at(0, 14, 30),
    location: 'Stanford Room',
    track: 'Data & Analytics',
    type: 'workshop',
    speakerIds: ['sp-3'],
  },
  {
    id: 's-6',
    title: 'Evening Reception',
    description: 'Cocktails, hors d’oeuvres, and conversation.',
    startsAt: at(0, 18, 0),
    endsAt: at(0, 20, 0),
    location: 'Rooftop Terrace',
    type: 'networking',
    speakerIds: [],
    saved: true,
  },
  {
    id: 's-7',
    title: 'Day 2 Keynote: Building a Brand That Compounds',
    description:
      'Why the strongest financial brands treat marketing as an investment, not an expense.',
    startsAt: at(1, 9, 0),
    endsAt: at(1, 10, 0),
    location: 'Grand Ballroom',
    track: 'Executive Briefing',
    type: 'keynote',
    speakerIds: ['sp-4'],
    saved: true,
  },
  {
    id: 's-8',
    title: 'Fireside Chat: Customer Experience as a Growth Engine',
    startsAt: at(1, 10, 30),
    endsAt: at(1, 11, 30),
    location: 'Grand Ballroom',
    track: 'Customer Experience',
    type: 'session',
    speakerIds: ['sp-5', 'sp-2'],
  },
  {
    id: 's-9',
    title: 'Closing Remarks & Farewell Lunch',
    startsAt: at(1, 12, 0),
    endsAt: at(1, 13, 30),
    location: 'Terrace Restaurant',
    type: 'meal',
    speakerIds: [],
  },
]

export const content: ContentItem[] = [
  {
    id: 'c-1',
    title: 'Executive Forum Program Guide',
    description: 'Full agenda, venue maps, and speaker bios in one PDF.',
    category: 'Event Essentials',
    kind: 'pdf',
    sizeLabel: '2.4 MB',
  },
  {
    id: 'c-2',
    title: '2026 Financial Marketing Outlook',
    description: 'Vericast’s flagship annual research report.',
    category: 'Research & Insights',
    kind: 'pdf',
    sizeLabel: '5.1 MB',
  },
  {
    id: 'c-3',
    title: 'Opening Keynote Slides',
    description: 'Presentation deck from Dr. Alicia Moreno.',
    category: 'Session Materials',
    kind: 'slides',
    availableFrom: 'Available after the session',
  },
  {
    id: 'c-4',
    title: 'Audience Intelligence Product Overview',
    description: 'How first-party data powers measurable growth.',
    category: 'Research & Insights',
    kind: 'video',
    sizeLabel: '8 min',
  },
  {
    id: 'c-5',
    title: 'Client Resource Portal',
    description: 'Access your account team and ongoing resources.',
    category: 'Event Essentials',
    kind: 'link',
  },
]

export const forms: EventForm[] = [
  {
    id: 'f-1',
    title: 'Meal Preferences',
    description:
      'Help us tailor catering to your needs for both days of the Forum.',
    type: 'meal',
    status: 'open',
    dueLabel: 'Due before Day 1',
    fields: [
      {
        id: 'diet',
        label: 'Dietary preference',
        type: 'select',
        required: true,
        options: [
          'No restrictions',
          'Vegetarian',
          'Vegan',
          'Gluten-free',
          'Kosher',
          'Halal',
        ],
      },
      {
        id: 'allergies',
        label: 'Allergies or other needs',
        type: 'textarea',
        helperText: 'Optional — let us know anything else our chefs should know.',
      },
    ],
  },
  {
    id: 'f-2',
    title: 'Questions for the Opening Keynote',
    description:
      'Submit a question for Dr. Alicia Moreno’s Q&A. Selected questions will be answered live.',
    type: 'qa',
    status: 'open',
    dueLabel: 'Open now',
    fields: [
      {
        id: 'question',
        label: 'Your question',
        type: 'textarea',
        required: true,
      },
      {
        id: 'attribute',
        label: 'May we attribute it to you?',
        type: 'radio',
        options: ['Yes, use my name', 'Keep it anonymous'],
      },
    ],
  },
  {
    id: 'f-3',
    title: 'Request a 1:1 Meeting',
    description:
      'Reserve time with a Vericast strategist during the Forum.',
    type: 'meeting_request',
    status: 'open',
    fields: [
      {
        id: 'topic',
        label: 'What would you like to discuss?',
        type: 'text',
        required: true,
      },
      {
        id: 'time',
        label: 'Preferred time',
        type: 'select',
        options: ['Day 1 – Lunch', 'Day 1 – Reception', 'Day 2 – Morning break'],
      },
    ],
  },
  {
    id: 'f-4',
    title: 'Post-Event Survey',
    description: 'Share your feedback to help us shape future Forums.',
    type: 'survey',
    status: 'closed',
    dueLabel: 'Opens after the event',
    fields: [
      {
        id: 'nps',
        label: 'How likely are you to recommend this event?',
        type: 'rating',
        required: true,
      },
    ],
  },
]
